<section class="sav-event-detail-page" aria-label="Event detail">
  <div class="sav-event-detail-wrap">
    <!-- The wrap carries no gutter on purpose: every other child is a full-bleed section that
         applies `--container-inline-pad` itself. The trail is the one ordinary in-flow child, so
         without a container of its own it sat flush against the wrap's edge — which is the site
         shell's edge, a few px LEFT of the viewport (the shell is 100vw wide to line full-bleed
         sections up past the scrollbar), so the first crumb was clipped. Every other page on the
         site puts its trail in `.sav-site-container`; this one now does too. -->
    <div class="sav-site-container">
      <app-breadcrumb [items]="crumbs()" />
    </div>
    <section class="sav-event-sale-banner" aria-label="Event promotion">
      <p class="sav-event-sale-banner-kicker">Big Deal</p>
      <div class="sav-event-sale-banner-row">
        <svg
          class="sav-event-sale-banner-sparkle"
          viewBox="0 0 48 48"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M24 2l5.1 13.9L43 21l-13.9 5.1L24 40l-5.1-13.9L5 21l13.9-5.1L24 2zm14.6 25.4l1.9 5.1 5.1 1.9-5.1 1.9-1.9 5.1-1.9-5.1-5.1-1.9 5.1-1.9 1.9-5.1zM11.4 27.4l1.9 5.1 5.1 1.9-5.1 1.9-1.9 5.1-1.9-5.1-5.1-1.9 5.1-1.9 1.9-5.1z"
          />
        </svg>
        <h1 class="type-on-dark">{{ eventTitle() }}</h1>
        <svg
          class="sav-event-sale-banner-sparkle"
          viewBox="0 0 48 48"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M24 2l5.1 13.9L43 21l-13.9 5.1L24 40l-5.1-13.9L5 21l13.9-5.1L24 2zm14.6 25.4l1.9 5.1 5.1 1.9-5.1 1.9-1.9 5.1-1.9-5.1-5.1-1.9 5.1-1.9 1.9-5.1zM11.4 27.4l1.9 5.1 5.1 1.9-5.1 1.9-1.9 5.1-1.9-5.1-5.1-1.9 5.1-1.9 1.9-5.1z"
          />
        </svg>
      </div>

      @if (eventShortDescription()) {
        <p class="sav-event-sale-banner-desc">{{ eventShortDescription() }}</p>
      }
    </section>

    @if (menuCategories().length) {
      <section class="sav-event-detail-menu-band" aria-label="Event sections">
        <div class="sav-event-detail-menu-wrap">
          <div class="sav-event-detail-menu-carousel">
            <button
              type="button"
              class="sav-event-detail-menu-arrow"
              aria-label="Scroll event menu left"
              (click)="scrollEventMenu(-1)"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M15 19L8 12L15 5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <div class="sav-event-detail-menu-viewport" #eventMenuViewport>
              <nav class="sav-event-detail-menu" aria-label="Event detail menu">
                @for (category of menuCategories(); track category.id) {
                  <button
                    type="button"
                    [id]="'event-menu-' + category.id"
                    [class.is-active]="isMenuCategoryActive(category)"
                    (click)="selectMenuCategory(category)"
                  >
                    {{ category.name }}
                  </button>
                }
              </nav>
            </div>

            <button
              type="button"
              class="sav-event-detail-menu-arrow"
              aria-label="Scroll event menu right"
              (click)="scrollEventMenu(1)"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 5L16 12L9 19"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </section>
    }

    @if (hasTopStoresFeatured()) {
      <section class="sav-top-stores-section" [attr.aria-label]="topStoresHeading()">
        <div class="sav-top-stores-wrap">
          <h2>{{ topStoresHeading() }}</h2>
          <p>The stores running the strongest offers in this event</p>
          <div class="sav-top-store-list" appAutoScrollRow>
            @for (store of topStoresFeatured(); track store.path + store.name) {
              <a class="sav-top-store" [routerLink]="store.path" [attr.aria-label]="store.name">
                <!-- The link's aria-label names the store, so the artwork is decorative. Eager: this
                     strip sits at the top of the page. -->
                <app-store-logo
                  class="sav-top-store-logo"
                  [logoUrl]="store.logoUrl"
                  [name]="store.name"
                  loading="eager"
                  decorative
                />
              </a>
            }
          </div>
        </div>
      </section>
    }

    @if (hasTopStores()) {
      <section class="sav-event-trending-stores-section" [attr.aria-label]="topStoresHeading()">
        <div class="sav-event-trending-stores-wrap">
          <div class="sav-trending-stores-grid">
            @for (store of topStores(); track store.path + store.name) {
              <a
                class="sav-trending-store-card"
                [routerLink]="store.path"
              >
                <span class="sav-store-tile-logo-box">
                  <app-store-logo [logoUrl]="store.logoUrl" [name]="store.name" decorative />
                </span>
                <span aria-hidden="true" class="sav-store-tile-divider"></span>
                <span class="sav-store-tile-store-meta">
                  <span class="sav-store-tile-brand-name">{{ store.name }}</span>
                  @if (store.couponsAndDealsCount) {
                    <span class="sav-store-tile-count">{{ store.couponsAndDealsCount }}</span>
                  }
                </span>
              </a>
            }
          </div>
        </div>
      </section>
    }

    @if (hasTopPickCoupons()) {
      <section
        id="event-cat-top-picks"
        class="sav-partner-coupons"
        aria-label="Partner offers and coupons"
      >
        <div class="sav-event-partner-coupons-wrap">
          <!-- `sav-section-head` is the site's heading + subtitle block: left aligned, H2 over a
               muted line. This was a centred custom header with its own eyebrow. -->
          <header class="sav-section-head">
            <h2>{{ currentCategoryCouponsHeading() }}</h2>
            <p>The strongest offers we have verified for this event.</p>
          </header>

          <!-- The same compact coupon card the category sections use, so this page shows one coupon
               design rather than two. -->
          <div class="sav-trending-coupons-grid">
            @for (item of topPickCoupons(); track item.id) {
              <article class="sav-trending-coupon-card">
                <a
                  class="sav-trending-coupon-brand"
                  [routerLink]="item.storePath || null"
                  [attr.aria-label]="item.store"
                >
                  <!-- `.sav-trending-coupon-brand-logo` stays on the slot: that global rule sizes it
                       and draws the ring. Decorative because the link's aria-label names the store. -->
                  <app-store-logo
                    class="sav-trending-coupon-brand-logo"
                    [logoUrl]="item.logoUrl"
                    [name]="item.store"
                    decorative
                  />
                </a>
                <div class="sav-trending-coupon-content">
                  <div class="sav-trending-coupon-top-row">
                    <span class="sav-trending-coupon-type">{{
                      item.isDeal ? 'Deal' : 'Code'
                    }}</span>
                    <span class="sav-trending-coupon-verified">Verified</span>
                  </div>
                  <a class="sav-trending-coupon-store" [routerLink]="item.storePath || null">{{
                    item.store
                  }}</a>
                  <h3 class="type-inherit">{{ item.title }}</h3>
                  <div class="sav-trending-coupon-bottom-row">
                    <span>Expires : {{ item.expires }}</span>
                    <button type="button" [class.deal]="item.isDeal" (click)="openEventOffer(item)">
                      {{ item.actionLabel }}
                    </button>
                  </div>
                </div>
              </article>
            }
          </div>
        </div>
      </section>
    }

    @for (section of eventCouponSections(); track section.id) {
      <section
        [id]="'event-cat-' + section.id"
        class="sav-trending-coupons-section"
        [attr.aria-label]="section.heading"
      >
        <div class="sav-trending-coupons-wrap">
          <header class="sav-section-head">
            <h2>{{ section.heading }}</h2>
            <p>Live codes and offers running during this event.</p>
          </header>
          @if (section.tabs.length) {
            <div
              class="sav-trending-coupons-tabs"
              [attr.aria-label]="'Subcategories for ' + section.heading"
            >
              @for (tab of section.tabs; track tab.id) {
                <button
                  type="button"
                  [class.active]="selectedCouponTab(section) === tab.id"
                  (click)="selectCouponTab(section.id, tab.id)"
                >
                  {{ tab.label }}
                </button>
              }
            </div>
          }

          <div class="sav-trending-coupons-grid">
            @for (item of visibleCoupons(section); track item.id) {
              <article class="sav-trending-coupon-card">
                <a
                  class="sav-trending-coupon-brand"
                  [routerLink]="item.storePath || null"
                  [attr.aria-label]="item.store"
                >
                  <!-- `.sav-trending-coupon-brand-logo` stays on the slot: that global rule sizes it
                       and draws the ring. Decorative because the link's aria-label names the store. -->
                  <app-store-logo
                    class="sav-trending-coupon-brand-logo"
                    [logoUrl]="item.logoUrl"
                    [name]="item.store"
                    decorative
                  />
                </a>
                <div class="sav-trending-coupon-content">
                  <div class="sav-trending-coupon-top-row">
                    <span class="sav-trending-coupon-type">{{
                      item.isDeal ? 'Deal' : 'Code'
                    }}</span>
                    <span class="sav-trending-coupon-verified">Verified</span>
                  </div>
                  <a class="sav-trending-coupon-store" [routerLink]="item.storePath || null">{{
                    item.store
                  }}</a>
                  <h3 class="type-inherit">{{ item.title }}</h3>
                  <div class="sav-trending-coupon-bottom-row">
                    <span>Expires : {{ item.expires }}</span>
                    <!-- Same two destinations as the store and home pages: a new tab gets the offer
                         popup, this tab follows the affiliate redirect. These used to be
                         plain links to the store page, so a click never reached the merchant. -->
                    <button type="button" [class.deal]="item.isDeal" (click)="openEventOffer(item)">
                      {{ item.actionLabel }}
                    </button>
                  </div>
                </div>
              </article>
            }
          </div>

          <div class="sav-trending-coupons-more">
            <a [routerLink]="section.viewMorePath"
              ><span>{{ section.viewMoreLabel }}</span
              ><svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                ></path></svg
            ></a>
          </div>
        </div>
      </section>
    }

    @if (hasEventProducts()) {
      <section class="sav-essentials" [attr.aria-label]="eventProductsHeading()">
        <div class="sav-event-essentials-wrap">
          <header class="sav-essentials-head">
            <div>
              <h2>{{ eventProductsHeading() }}</h2>
              <p>Curated Collection</p>
            </div>
          </header>
          <div class="sav-product-grid">
            @for (product of eventProducts(); track product.id || product.name) {
              <app-product-card
                [title]="product.name"
                [image]="product.imageUrl"
                [hoverImage]="product.hoverImageUrl"
                [storeName]="product.storeName"
                [storeLogoUrl]="product.storeLogoUrl"
                [price]="product.newPrice"
                [oldPrice]="product.oldPrice"
                [href]="product.storePath"
                [external]="product.isExternal"
                [productId]="product.id"
              />
            }
          </div>
        </div>
      </section>
    }

    @if (visiblePopularCategories().length > 0) {
      <section class="sav-popular-categories-section" aria-label="Popular categories">
        <div class="sav-popular-categories-wrap">
          <h2>{{ currentCategoryPopularHeading() }}</h2>
          <p>Browse this event by the category you are shopping.</p>
          <div class="sav-popular-categories-tabs" role="tablist" aria-label="Category feeds">
            @for (tab of popularCategoryTabs(); track tab.id) {
              <button
                type="button"
                role="tab"
                [class.active]="selectedPopularCategoryTabId() === tab.id"
                [id]="'popular-categories-tab-' + tab.id"
                [attr.aria-selected]="selectedPopularCategoryTabId() === tab.id"
                [attr.aria-controls]="'popular-categories-panel-' + tab.id"
                (click)="selectPopularCategoryTab(tab.id)"
              >
                {{ tab.label }}
              </button>
            }
          </div>

          <div
            class="sav-popular-categories-grid"
            role="tabpanel"
            [id]="'popular-categories-panel-' + selectedPopularCategoryTabId()"
            [attr.aria-labelledby]="'popular-categories-tab-' + selectedPopularCategoryTabId()"
          >
            @for (category of visiblePopularCategories(); track category.label + $index) {
              <a
                [class]="'sav-popular-category-card ' + (category.thumb || '')"
                [routerLink]="category.path || null"
              >
                <span class="sav-popular-category-thumb" aria-hidden="true">
                  @if (category.imageUrl) {
                    <img [src]="category.imageUrl" [alt]="category.label" loading="lazy" />
                  } @else if (category.iconHtml) {
                    <span class="sav-popular-category-icon" [innerHTML]="category.iconHtml"></span>
                  }
                </span>
                <span class="sav-popular-category-label">{{ category.label }}</span>
              </a>
            }
          </div>

          <div class="sav-popular-categories-dots" aria-hidden="true">
            @for (index of popularCategorySlideIndexes(); track index) {
              <button
                type="button"
                [class.active]="currentPopularCategorySlide() === index"
                [attr.aria-label]="'Go to category slide ' + (index + 1)"
                (click)="selectPopularCategorySlide(index)"
              ></button>
            }
          </div>
        </div>
      </section>
    }

    <!-- The site-wide newsletter/event CTA, the same one the home page ends with. It replaced a
         static "Premium Savings Just For You" spotlight whose buttons were dead `href="#"`, whose
         "Join 4,200+ others saving right now" counter was invented, and whose emoji had been
         double-encoded into mojibake. The category page had already made this same swap. -->
    <app-big-sale-banner />

    @if (hasEventBlogs()) {
      <section class="sav-journal-grid-section" aria-label="Event insights and guides">
        <div class="sav-journal-grid-wrap">
          <div class="sav-journal-grid-head">
            <h3>{{ eventBlogsHeading() }}</h3>
            <p>Guides, tips, and shopping strategies to help you save more during this event.</p>
          </div>

          <div class="sav-journal-grid" role="list">
            @for (post of eventBlogs(); track post.path + post.title) {
              <article class="sav-journal-card" role="listitem">
                <div class="sav-journal-card-media">
                  @if (post.imageUrl) {
                    <img [src]="post.imageUrl" [alt]="post.title" />
                  }
                  @if (post.category) {
                    <span class="sav-journal-media-badge">{{ post.category }}</span>
                  }
                </div>

                <div class="sav-journal-card-body">
                  @if (post.category) {
                    <span class="sav-journal-category-chip">{{ post.category }}</span>
                  }

                  <div class="sav-journal-meta-row">
                    <span>{{ post.date }}</span>
                    <span>{{ post.readTime }}</span>
                  </div>

                  <h4>{{ post.title }}</h4>
                  <p class="sav-journal-line-clamp-3">{{ post.excerpt }}</p>

                  <div class="sav-journal-card-footer">
                    <div class="sav-journal-author">
                      <span class="sav-journal-author-avatar">{{ post.author.charAt(0) }}</span>
                      <span>{{ post.author }}</span>
                    </div>
                    <a [routerLink]="post.path">Read More</a>
                  </div>
                </div>
              </article>
            }
          </div>
        </div>
      </section>
    }

    @if (hasEventStores()) {
      <section class="sav-trending-stores-section" [attr.aria-label]="eventStoresHeading()">
        <div class="sav-trending-stores-wrap">
          <h2>{{ eventStoresHeading() }}</h2>
          <p>Retailers taking part in this event.</p>
          <div class="sav-trending-stores-grid">
            @for (store of eventStores(); track store.path + store.name) {
              <a
                class="sav-trending-store-card"
                [routerLink]="store.path"
              >
                <span class="sav-store-tile-logo-box">
                  <app-store-logo [logoUrl]="store.logoUrl" [name]="store.name" decorative />
                </span>
                <span class="sav-store-tile-divider" aria-hidden="true"></span>
                <span class="sav-store-tile-store-meta">
                  <span class="sav-store-tile-brand-name">{{ store.name }}</span>
                  @if (store.couponsAndDealsCount) {
                    <span class="sav-store-tile-count">{{ store.couponsAndDealsCount }}</span>
                  }
                </span>
              </a>
            }
          </div>
        </div>
      </section>
    }

    @if (hasEventFaqs()) {
      <section class="sav-faq-section" aria-label="Frequently Asked Questions">
        <div class="sav-faq-wrap">
          <h2>{{ eventFaqsHeading() }}</h2>
          <p>Common questions shoppers ask about this event.</p>
          <div class="sav-faq-list">
            @for (faq of eventFaqs(); track $index) {
              <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === $index">
                <button
                  class="sav-faq-question"
                  type="button"
                  [attr.aria-expanded]="expandedFaqIndex() === $index"
                  (click)="toggleFaq($index)"
                >
                  <span>{{ faq.question }}</span>
                  <svg
                    class="sav-faq-chevron"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === $index">
                  <p>{{ faq.answer }}</p>
                </div>
              </div>
            }
          </div>
        </div>
      </section>
    }

    @if (hasEventLongContent()) {
      <section class="sav-longform" aria-label="Complete savings guide">
        <div class="sav-event-longform-wrap">
          <div class="sav-event-longform-card">
            <header class="sav-longform-head">
              <h2>The Complete {{ eventTitle() }} Guide</h2>
              <p>Everything worth knowing before this sale starts.</p>
            </header>

            <!-- Printed in full, clamped by CSS. See the same block on the store page. -->
            <input
              type="checkbox"
              class="sav-readmore-input"
              id="sav-event-longform-readmore"
              aria-controls="sav-event-longform-readmore-body"
              aria-label="Show the full event guide"
            />
            <div
              id="sav-event-longform-readmore-body"
              class="sav-longform-content sav-readmore-rich"
              [class.is-clamped]="shouldShowLongformToggle"
              [innerHTML]="eventLongHtml() | trustedCmsHtml"
            ></div>

            @if (shouldShowLongformToggle) {
              <label class="sav-longform-toggle" for="sav-event-longform-readmore">
                <span class="sav-readmore-more">Read More</span>
                <span class="sav-readmore-less">Read Less</span>
                <span class="sav-longform-toggle-icon" aria-hidden="true">+</span>
              </label>
            }
          </div>
        </div>
      </section>
    }

    @if (seasonalEvents().length) {
      <section class="sav-seasonal-events-section" aria-label="Seasonal shopping events">
        <div class="sav-seasonal-events-wrap">
          <h2>Seasonal Shopping Events</h2>
          <p>Plan ahead for the sale windows worth waiting for.</p>

          <div class="sav-seasonal-events-grid">
            @for (
              seasonalEvent of seasonalEvents();
              track seasonalEvent.path + seasonalEvent.name
            ) {
              <a class="sav-seasonal-event-chip" [routerLink]="seasonalEvent.path">
                {{ seasonalEvent.name }}
              </a>
            }
          </div>
        </div>
      </section>
    }
  </div>
</section>
